import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Heart, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAuth } from '@/contexts/AuthContext';
import { listingsAPI } from '@/lib/api';
import { toast } from 'sonner';
import { cn, getFullImageUrl } from '@/lib/utils';
import { Link } from 'react-router-dom';

export const LikeButton = forwardRef(({ 
    listingId, 
    initialLikesCount = 0, 
    size = 'default', 
    showCount = true,
    onLikesCountChange,
    className
}, ref) => {
    const { isAuthenticated } = useAuth();
    const [liked, setLiked] = useState(false);
    const [likesCount, setLikesCount] = useState(initialLikesCount);
    const [loading, setLoading] = useState(false);
    const [showLikesModal, setShowLikesModal] = useState(false);
    const [likedUsers, setLikedUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);

    // Expose openModal function via ref
    useImperativeHandle(ref, () => ({
        openModal: () => {
            if (likesCount > 0) {
                setShowLikesModal(true);
                fetchLikedUsers();
            }
        },
        getLikesCount: () => likesCount
    }));

    // Check initial like status
    useEffect(() => {
        if (isAuthenticated && listingId) {
            checkLikeStatus();
        }
    }, [isAuthenticated, listingId]);

    // Update parent when likes count changes
    useEffect(() => {
        if (onLikesCountChange) {
            onLikesCountChange(likesCount);
        }
    }, [likesCount, onLikesCountChange]);

    const checkLikeStatus = async () => {
        try {
            const response = await listingsAPI.getLikeStatus(listingId);
            setLiked(response.data.liked);
            setLikesCount(response.data.likes_count);
        } catch (error) {
            // Silently fail - user might not be authenticated
        }
    };

    const handleLikeToggle = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!isAuthenticated) {
            toast.error('Trebuie să fii autentificat pentru a da like');
            return;
        }

        setLoading(true);
        try {
            const response = await listingsAPI.toggleLike(listingId);
            setLiked(response.data.liked);
            setLikesCount(response.data.likes_count);
            
            if (response.data.liked) {
                toast.success('Ai apreciat această piesă!');
            }
        } catch (error) {
            toast.error('Eroare la procesarea like-ului');
        } finally {
            setLoading(false);
        }
    };

    const fetchLikedUsers = async () => {
        setLoadingUsers(true);
        try {
            const response = await listingsAPI.getLikes(listingId);
            setLikedUsers(response.data.users || []);
            setLikesCount(response.data.likes_count);
            setLiked(response.data.liked_by_me);
        } catch (error) {
            toast.error('Eroare la încărcarea like-urilor');
        } finally {
            setLoadingUsers(false);
        }
    };

    const handleOpenModal = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (likesCount > 0) {
            setShowLikesModal(true);
            fetchLikedUsers();
        }
    };

    const sizeClasses = {
        xs: 'h-8 w-8',
        sm: 'h-8 w-8',
        default: 'h-10 w-10',
        lg: 'h-12 w-12'
    };

    const iconSizes = {
        xs: 'w-4 h-4',
        sm: 'w-4 h-4',
        default: 'w-5 h-5',
        lg: 'w-6 h-6'
    };

    return (
        <div className={cn("flex items-center gap-2", className)}>
            {/* Like Button */}
            <Button
                variant={size === 'xs' ? 'ghost' : 'outline'}
                size="icon"
                onClick={handleLikeToggle}
                disabled={loading}
                className={cn(
                    sizeClasses[size],
                    "rounded-full transition-all duration-200",
                    size === 'xs' && "bg-white/90 backdrop-blur-xs border-0",
                    liked 
                        ? "bg-red-50 border-red-200 text-red-500 hover:bg-red-100 hover:text-red-600" 
                        : "hover:border-red-200 hover:text-red-500"
                )}
                data-testid={`like-btn-${listingId}`}
            >
                <Heart 
                    className={cn(
                        iconSizes[size],
                        "transition-transform",
                        liked && "fill-current scale-110"
                    )} 
                />
            </Button>

            {/* Likes Count - clickable to show who liked */}
            {showCount && (
                <>
                    <button
                        onClick={handleOpenModal}
                        className={cn(
                            "text-sm text-muted-foreground hover:text-foreground transition-colors",
                            likesCount > 0 && "cursor-pointer hover:underline"
                        )}
                        disabled={likesCount === 0}
                    >
                        {likesCount} {likesCount === 1 ? 'apreciere' : 'aprecieri'}
                    </button>
                </>
            )}

            {/* Likes Modal */}
            <Dialog open={showLikesModal} onOpenChange={setShowLikesModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Heart className="w-5 h-5 text-red-500 fill-current" />
                            Aprecieri ({likesCount})
                        </DialogTitle>
                    </DialogHeader>
                    
                    {loadingUsers ? (
                        <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                        </div>
                    ) : likedUsers.length > 0 ? (
                        <ScrollArea className="max-h-[400px]">
                            <div className="space-y-3 pr-4">
                                {likedUsers.map((likeUser) => (
                                    <Link
                                        key={likeUser.user_id}
                                        to={`/user/${likeUser.username}`}
                                        onClick={() => setShowLikesModal(false)}
                                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted transition-colors"
                                    >
                                        <Avatar className="h-10 w-10">
                                            <AvatarImage 
                                                src={likeUser.picture ? getFullImageUrl(likeUser.picture) : undefined} 
                                                alt={likeUser.full_name} 
                                            />
                                            <AvatarFallback className="bg-primary text-white">
                                                {likeUser.full_name?.[0]?.toUpperCase() || '?'}
                                            </AvatarFallback>
                                        </Avatar>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-foreground truncate">
                                                {likeUser.full_name}
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                                {new Date(likeUser.liked_at).toLocaleDateString('ro-RO', {
                                                    day: 'numeric',
                                                    month: 'short',
                                                    year: 'numeric'
                                                })}
                                            </p>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </ScrollArea>
                    ) : (
                        <div className="text-center py-8 text-muted-foreground">
                            <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                            <p>Nicio apreciere încă</p>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
});

LikeButton.displayName = 'LikeButton';

export default LikeButton;
